import { useEffect, useState } from 'react'
import { getServiceLabel } from '../helpers/services.helper'
import { t } from '../locale'
import { fetchServiceUsageHistory, ServiceUsageLog } from '../service/subscription'
import { Button, Modal } from 'react-bootstrap'

function LastActivitiesCard() {
  const [activities, setActivities] = useState<ServiceUsageLog[] | null>(null)
  const [selectedActivity, setSelectedActivity] = useState<ServiceUsageLog | null>(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    fetchServiceUsageHistory(10).then(data => {
      setActivities(data)
    })
  }, [])

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return t('justNow') || 'just now'
    if (diffMins < 60) return `${diffMins}m`
    if (diffHours < 24) return `${diffHours}h`
    if (diffDays < 7) {
      return diffDays === 1 ? 'vor 1 Tag' : `vor ${diffDays} Tagen`
    }

    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  const handleActivityClick = (activity: ServiceUsageLog) => {
    setSelectedActivity(activity)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedActivity(null)
  }

  return (
    <>
      <div className='border border-0 rounded-lg p-4 bg-white shadow-sm mb-4 h-100 d-flex flex-column'>
        <div className='fw-bold text-draft mb-3 fs-5 border-bottom pb-2'>{t('lastActivities')}</div>

        <div className='flex-grow-1 d-flex flex-column justify-content-start'>
          {activities === null && (
            <div className='text-muted small text-center py-3'>{t('Loading...')}</div>
          )}

          {activities?.length === 0 && (
            <div className='text-muted small text-center py-3'>{t('noData')}</div>
          )}

          {!!activities?.length && (
            <ul
              className='list-unstyled mb-0'
              style={{ fontSize: '0.75rem', maxHeight: '300px', overflowY: 'auto' }}
            >
              {activities.map((activity, index) => (
                <li
                  key={index}
                  className='border-bottom py-3 d-flex align-items-center justify-content-between gap-2'
                  style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  onClick={() => handleActivityClick(activity)}
                >
                  <div className='text-muted fw-500'>{t(getServiceLabel(activity.service))}</div>
                  <div className='text-muted ps-3 flex-shrink-0 badge bg-light text-dark'>
                    {formatDate(activity.usedAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton className='border-bottom'>
          <Modal.Title className='fw-bold'>{t('activityDetails')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedActivity && (
            <div>
              <div className='mb-3 p-2 bg-light rounded'>
                <strong className='text-primary'>{t('service')}:</strong>
                <span className='ms-2 text-dark'>
                  {t(getServiceLabel(selectedActivity.service))}
                </span>
              </div>
              <div className='mb-3 p-2 bg-light rounded'>
                <strong className='text-primary'>{t('userName')}:</strong>
                <span className='ms-2 text-dark'>{selectedActivity.userName}</span>
              </div>
              <div className='mb-3 p-2 bg-light rounded'>
                <strong className='text-primary'>{t('email')}:</strong>
                <span className='ms-2 text-dark'>{selectedActivity.userEmail}</span>
              </div>
              {selectedActivity.pointsDeducted !== undefined && (
                <div className='mb-3 p-2 bg-light rounded'>
                  <strong className='text-primary'>{t('pointsUsed')}:</strong>
                  <span className='ms-2 text-dark'>{selectedActivity.pointsDeducted}</span>
                </div>
              )}
              <div className='p-2 bg-light rounded'>
                <strong className='text-primary'>{t('time')}:</strong>
                <span className='ms-2 text-dark'>
                  {new Date(selectedActivity.usedAt).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='primary' onClick={handleCloseModal}>
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default LastActivitiesCard
